import { useQuery, useSuspenseQueries } from '@tanstack/react-query';
import { useState } from 'react';
import { maxAPIKeysQueryOptions } from '#api/api-keys.queries.ts';
import { maxEndppointOIDCProvidersQueryOptions } from '#api/endpoint-oidc-providers.queries.ts';
import { ValidationError } from '#api/errors.ts';
import { makeHostsQueryOptions } from '#api/hosts.queries.ts';
import { maxJoinKeysQueryOptions } from '#api/join-keys.queries.ts';
import { useSuspenseFeatureFlagsQuery } from '#api/org.queries.ts';
import { makeRoleQueryOptions } from '#api/roles.queries.ts';
import { Code } from '#components/atoms/code/index.ts';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { ConfirmDialog } from '#components/patterns/confirmations/confirm-dialog/ConfirmDialog.tsx';
import { DeleteRoleError } from './DeleteRoleError.tsx';
import { useDeleteRoleMutation } from './useDeleteRoleMutation.ts';

type Props = {
  roleID: string;
  onCancel: () => void;
  onDelete: () => void;
};

export function DeleteRoleDialog({ roleID, onCancel, onDelete }: Props) {
  const [validationErrors, setValidationErrors] = useState<string[] | null>(null);
  const deleteRole = useDeleteRoleMutation();
  const { data: { ROLE_SCOPED_API_KEYS: canScopeToRole } = {} } = useSuspenseFeatureFlagsQuery();
  const [
    {
      data: {
        data: firstFiveHostsWithRole,
        metadata: { totalCount: totalHostsWithRole },
      },
    },
    {
      data: { data: endpointOIDCProviders },
    },
  ] = useSuspenseQueries({
    queries: [makeHostsQueryOptions(undefined, 5, { roleID }), maxEndppointOIDCProvidersQueryOptions],
  });
  // Without ROLE_SCOPED_API_KEYS no key can be scoped to a role, so there is nothing to look up.
  const apiKeysQuery = useQuery({ ...maxAPIKeysQueryOptions, enabled: Boolean(canScopeToRole) });
  const joinKeysQuery = useQuery(maxJoinKeysQueryOptions);
  const providersWithRole = endpointOIDCProviders.filter((provider) => provider.roleID === roleID);
  const apiKeysWithRole = apiKeysQuery.data?.data.filter((apiKey) => apiKey.roleID === roleID) ?? [];
  const joinKeysWithRole = joinKeysQuery.data?.data.filter((joinKey) => joinKey.roleID === roleID) ?? [];
  const { data: { data: role } = {} } = useQuery({
    ...makeRoleQueryOptions(roleID),
    // HACK: there's a single render after deleting, which causes a 404.  We avoid that with some extra state.  :(
    enabled: !deleteRole.isSuccess,
  });

  // Clears out the validation error message, in case the modal is re-opened.
  function closeModal() {
    setValidationErrors(null);
    onCancel();
  }

  // Wait until we have the role details, its join keys, and, if they can exist, its API keys
  if (!roleID || !role || (canScopeToRole && apiKeysQuery.isPending) || joinKeysQuery.isPending) {
    return null;
  }

  if (
    firstFiveHostsWithRole.length > 0 ||
    providersWithRole.length > 0 ||
    apiKeysWithRole.length > 0 ||
    joinKeysWithRole.length > 0
  ) {
    // Show an error dialog with links to whatever still depends on this role.
    return (
      <DeleteRoleError
        role={role}
        firstFiveHostsWithRole={firstFiveHostsWithRole}
        totalHostsWithRole={totalHostsWithRole}
        endpointOIDCProviders={providersWithRole}
        apiKeys={apiKeysWithRole}
        joinKeys={joinKeysWithRole}
        onClose={closeModal}
      />
    );
  }

  return (
    <ConfirmDialog
      title={<div className="truncate">Delete {role.name}?</div>}
      disableAccept={deleteRole.isPending || !role}
      acceptButtonText="Delete"
      onCancel={closeModal}
      onAccept={async () => {
        setValidationErrors(null);

        try {
          await deleteRole.mutateAsync(roleID, {
            onSuccess: onDelete,
          });
        } catch (error: unknown) {
          if (error instanceof ValidationError) {
            setValidationErrors(error.errors.map((e) => e.message));
          }
        }
      }}
      shouldRestoreFocusViaFocusScope={true}
    >
      <p className="p mb-0 mt-0">
        Are you sure you want to delete <Code className="ml-6 wrap-anywhere">{role.name}</Code>?
      </p>

      {role.description && (
        <div className="mt-32">
          <div className="mb-8">Role description:</div>
          {role.description}
        </div>
      )}

      {Boolean(validationErrors?.length) &&
        validationErrors.map((message, index) => (
          // eslint-disable-next-line react/no-array-index-key -- It's fine, these aren't going to move/change
          <ErrorMessage key={index} className={index === 0 ? 'mt-32' : undefined}>
            {message}
          </ErrorMessage>
        ))}
    </ConfirmDialog>
  );
}
